import {
  SurveyFormData,
  TRaceObj,
  TSensitiveCriteria,
} from "../survey/SurveyContainer";
import { isEqual, omit } from "lodash";
import {
  Affiliations,
  ScholarshipCriteria,
  ScholarshipCriteriaInput,
  CurrentEnrollments,
  TestScores,
  SensitiveCriteriaInput,
} from "../api";
import { isEmptyValues } from "./index";

const filterDeep = require("deepdash/filterDeep");

const mapCollegeData = (college: Omit<CurrentEnrollments, "__typename">) => {
  return {
    schoolName: college?.schoolName || "",
    ncesCode: college?.ncesCode || "",
    ceebCode: college?.ceebCode || "",
    schoolType: college?.schoolType || "",
    schoolAddress: {
      addressLine1: college?.schoolAddress?.addressLine1 || "",
      addressLine2: college?.schoolAddress?.addressLine2 || "",
      city: college?.schoolAddress?.city || "",
      stateOrProvince: college?.schoolAddress?.stateOrProvince || "",
      zipOrPostalCode: college?.schoolAddress?.zipOrPostalCode || "",
      country: college?.schoolAddress?.country || "",
    },
  };
};

const mapAffilitions = (affiliation: Omit<Affiliations, "__typename">) => {
  return {
    entityName: affiliation.entityName || "",
    directRelation: affiliation.directRelation || "",
    indirectRelation: affiliation.indirectRelation || "",
  };
};

export function transformSensitiveCriteriaToSchemaData(
  sensitiveCriteria: TSensitiveCriteria
): SensitiveCriteriaInput {
  const flatRaceArray = Object.keys(sensitiveCriteria.race).filter(
    (k) => sensitiveCriteria.race[k]
  );

  return {
    gender: sensitiveCriteria.gender,
    sexuality: sensitiveCriteria.sexuality,
    race: flatRaceArray,
    ancestry: sensitiveCriteria.ancestry,
    ethnicity: sensitiveCriteria.ethnicity,
    citizenshipStatuses: sensitiveCriteria.citizenshipStatuses,
    condition: sensitiveCriteria.condition,
  };
}

/**
 * Converts backend data format into frontend form format.
 * Omits the sensitive items because those do not come from GraphQL.
 * @param criteria Data returned from backend
 * @returns Data in format which form uses
 */
export function transformSchemaDataToFormData(
  criteria: ScholarshipCriteria
): Omit<SurveyFormData, "sensitive"> {
  const raceObj = criteria.demographics?.race?.reduce(
    (acc: TRaceObj, curr: string | null) => {
      if (curr) {
        acc[curr] = true;
      }
      return acc;
    },
    {}
  );
  // Since these are being fed into controlled components w/ RHF state logic, we need default non-null values
  const currentLocation = {
    city: criteria?.currentLocation?.city || "",
    county: criteria?.currentLocation?.county || "",
    country: criteria?.currentLocation?.country || "US",
    state: criteria?.currentLocation?.state || "",
  };
  const permanentLocation = {
    city: criteria?.permanentLocation?.city || "",
    county: criteria?.permanentLocation?.county || "",
    country: criteria?.permanentLocation?.country || "US",
    state: criteria?.permanentLocation?.state || "",
  };
  const sameAsCurrent = isEqual(currentLocation, permanentLocation);

  const collegeChoices = criteria.collegeChoices?.map((choice) => {
    return mapCollegeData(choice);
  }) || [
    {
      schoolName: "",
    },
  ];

  const currentAndIntendedMajors = criteria.currentAndIntendedMajors?.map(
    (major) => {
      return {
        majorName: major?.majorName || "",
        majorCIPCode: major?.majorCIPCode || "",
      };
    }
  ) || [
    {
      majorName: "",
      majorCIPCode: "",
    },
  ];

  const schoolAndMajorChoices = collegeChoices.map((choice, idx) => {
    return {
      ...choice,
      ...currentAndIntendedMajors[idx],
    };
  });

  const currentEnrollments = criteria.currentEnrollments?.map(
    (currentSchool) => {
      return mapCollegeData(currentSchool);
    }
  ) || [mapCollegeData({})];

  const affiliations = criteria.affiliations
    ?.filter(
      (a) =>
        a.entityName !== "" &&
        a.directRelation !== "" &&
        a.indirectRelation !== ""
    )
    .map((affiliation) => {
      return mapAffilitions(affiliation);
    }) || [mapAffilitions({})];

  // Page 2
  let testScores: Omit<TestScores, "__typename">[] = [
    ...(criteria.academics?.testScores || []),
  ];
  if (testScores.length === 0) {
    // Make sure there is at least one test score so the UI will show it
    testScores.push({
      test: "",
      score: 0,
    });
  }

  return {
    demographics: {
      age: Number(criteria.age) || "",
      gender: criteria.demographics?.gender || "",
      citizenshipStatuses: criteria.citizenshipStatuses || [],
      sexuality: criteria.demographics?.genderIdentity || "",
      ancestry: criteria.demographics?.ancestry || [],
      race: raceObj || {},
      ethnicity: criteria.demographics?.ethnicity || "",
      currentLocation,
      permanentLocation: {
        ...permanentLocation,
        same_as_current: sameAsCurrent,
      },
    },
    collegeInfo: {
      collegeReadinessPrograms: criteria.collegeReadinessPrograms || [],
      schoolAndMajorChoices,
      currentEnrollments,
      enrollmentStatus: criteria.enrollmentStatus?.[0] || "",
      scholarshipPurpose: criteria.scholarshipPurpose || "",
    },
    academicInfo: {
      currentSchool: {
        gradeLevel: criteria.currentGradeLevel || "",
        gpa: Number(criteria.academics?.cumulativeGPA) || "",
        classRank: Number(criteria.academics?.classRankPercentile) || "",
      },
      testScores: testScores.map((score) => {
        return {
          test: score.test || "",
          score: Number(score.score) || "",
        };
      }),
      activities: criteria.activity || [],
      interests: criteria.interests || [],
      graduationStatus: {
        graduated:
          criteria.graduationInfo?.graduated !== null &&
          criteria.graduationInfo?.graduated !== undefined
            ? criteria.graduationInfo.graduated.toString()
            : "",
        graduationType: criteria.graduationInfo?.graduationStatus || "",
        graduationDate:
          criteria.graduationInfo?.graduationDate?.toString() || "",
        school: criteria.graduationInfo?.school
          ? mapCollegeData(criteria.graduationInfo.school)
          : mapCollegeData({}),
      },
    },
    additionalInfo: {
      affiliations,
      condition: criteria.condition || [],
      situation: criteria.situation || [],
      miscellaneous: criteria.miscellaneous || [],
    },
  };
}

export function transformFormDataToSchemaData(
  formData: Omit<SurveyFormData, "sensitive">
): ScholarshipCriteriaInput {
  const { demographics, collegeInfo, academicInfo, additionalInfo } = formData;

  // Page 1 - Demographics
  const demographicSchemaData = {
    age: Number(demographics.age),
    currentLocation: demographics.currentLocation,
    permanentLocation: demographics.permanentLocation.same_as_current
      ? demographics.currentLocation
      : omit(demographics.permanentLocation, "same_as_current"),
  };

  // Page 2 - Academic Info
  const academicInfoSchemaData = {
    currentGradeLevel: academicInfo.currentSchool.gradeLevel,
    academics: {
      testScores: academicInfo.testScores
        .filter((score) => score.test !== "" && score.score !== "")
        .map((score) => {
          return {
            test: score.test,
            score: score.score,
          };
        }),
      cumulativeGPA: academicInfo.currentSchool.gpa,
      classRankPercentile: academicInfo.currentSchool.classRank,
    },
    activity: academicInfo.activities,
    interests: academicInfo.interests,
    graduationInfo: {
      graduated:
        academicInfo.graduationStatus.graduated !== ""
          ? academicInfo.graduationStatus.graduated === "true"
          : undefined,
      // We need to cast graduationDate here to a string, in order for it to be read by the frontend and
      // not be stripped out by filterDeep later on in this code
      graduationDate: academicInfo.graduationStatus.graduationDate?.toString(),
      graduationStatus: academicInfo.graduationStatus.graduationType,
      school:
        academicInfo.graduationStatus.graduated === "true"
          ? academicInfo.graduationStatus.school
          : undefined,
    },
  };

  // Page 3 - College Info
  // These maps exist because we create our own data structure to represent
  // the school and major choice combinations displayed within the schoolAndMajorChoices AddAnother component tree
  const collegeChoices =
    collegeInfo.schoolAndMajorChoices?.map((schoolAndMajorChoice) => {
      return {
        schoolName: schoolAndMajorChoice.schoolName,
      };
    }) || [];

  const currentAndIntendedMajors =
    collegeInfo.schoolAndMajorChoices?.map((schoolAndMajorChoice) => {
      return {
        majorName: schoolAndMajorChoice.majorName,
      };
    }) || [];

  const collegeInfoSchemaData = {
    collegeReadinessPrograms: collegeInfo.collegeReadinessPrograms,
    collegeChoices,
    currentAndIntendedMajors,
    currentEnrollments: collegeInfo.currentEnrollments,
    enrollmentStatus: [collegeInfo.enrollmentStatus],
    scholarshipPurpose: collegeInfo.scholarshipPurpose,
  };

  //Page 4 - Additional info
  // Handling AddAnother component tree
  const affiliations =
    additionalInfo.affiliations?.map((affiliation) => {
      return {
        entityName: affiliation.entityName,
        directRelation: affiliation.directRelation,
        indirectRelation: affiliation.indirectRelation,
      };
    }) || [];

  const additionalInfoSchemaData = {
    affiliations,
    situation: additionalInfo.situation,
    miscellaneous: additionalInfo.miscellaneous,
  };

  // Wrap it up
  const criteria = {
    ...demographicSchemaData,
    ...academicInfoSchemaData,
    ...additionalInfoSchemaData,
    ...collegeInfoSchemaData,
  };

  return filterDeep(criteria, (value: string | number | [] | object) => {
    return !isEmptyValues(value);
  });
}
